import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class KeyClickService {
  private pressedKeys = new BehaviorSubject<{ [key: string]: boolean }>({});
  /**
   * A set to keep track of key combinations for which the default behavior should be prevented.
   *
   * Combinations are added as a comma separated string. (e.g. 'Control,Enter')
   */
  private preventDefaultKeyCombos = new Set<string>();
  lastShortcutTime = 0;
  keyDown$ = fromEvent<KeyboardEvent>(window, 'keydown');
  keyUp$ = fromEvent<KeyboardEvent>(window, 'keyup');

  /**
   * Observable that emits 'true' when a shortcut key combination is pressed.
   * Uses distinctUntilChanged to emit only once on initial press, ignoring OS key repeat events.
   *
   * This observable checks for the following key combinations:
   * - Control + Enter
   * - Meta (Command on Mac) + Enter
   * - Control + Space
   */
  shortcutPressed$ = this.pressedKeys.pipe(
    map(keys => this.hasShortcut(keys)),
    distinctUntilChanged(),
    filter(hasShortcut => hasShortcut)
  );

  /**
   * Observable that emits 'true' when only the Enter key is pressed.
   * This observable filters out any other key presses and only emits when Enter is the only key pressed.
   */
  enterOnlyPressed$ = this.pressedKeys.pipe(
    map(keys => this.isOnlyKey('Enter')),
    filter(enterPressed => enterPressed)
  );

  constructor() {
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.subscribeToKeyClick();
  }

  hasPressedKey(key: string): boolean {
    return !!this.pressedKeys.getValue()[key];
  }

  hasShortcutHeld(): boolean {
    return this.hasShortcut(this.pressedKeys.getValue());
  }

  preventDefaultOn(keys: string) {
    const sortedKeys = keys
      .split(',')
      .map(key => key.trim())
      .sort()
      .join(',');
    this.preventDefaultKeyCombos.add(sortedKeys);
  }

  resumeDefaultOn(key: string) {
    this.preventDefaultKeyCombos.delete(key);
  }

  isShortcutActive(timeoutMs: number): boolean {
    return this.hasShortcutHeld() && Date.now() - this.lastShortcutTime < timeoutMs;
  }

  private handleKeyDown(event: KeyboardEvent) {
    // Reconcile modifier state from the event — Mac often drops keyup
    // events for keys released while Meta is held.
    const updatedKeys = { ...this.pressedKeys.getValue(), [event.key]: true };

    // If the browser says Meta is NOT pressed but we still have it, remove it
    if (!event.metaKey && updatedKeys.Meta && event.key !== 'Meta') {
      delete updatedKeys.Meta;
    }
    // If the browser says Ctrl is NOT pressed but we still have it, remove it
    if (!event.ctrlKey && updatedKeys.Control && event.key !== 'Control') {
      delete updatedKeys.Control;
    }

    this.pressedKeys.next(updatedKeys);

    if (this.hasShortcutHeld()) {
      this.lastShortcutTime = Date.now();
    }

    if (this.preventDefaultOnKeyCombo()) {
      event.preventDefault();
    }
  }

  private handleKeyUp(event: KeyboardEvent) {
    console.log('key up', event.key);
    let updatedKeys: { [key: string]: boolean };

    // When Meta (Cmd) is released on Mac, also clear Enter — Mac suppresses
    // the Enter keyup while Meta was held, leaving a ghost "Enter" in state.
    if (event.key === 'Meta') {
      const { Meta: _m, Enter: _e, ...rest } = this.pressedKeys.getValue();
      updatedKeys = rest;
    } else {
      const { [event.key]: removed, ...rest } = this.pressedKeys.getValue();
      updatedKeys = rest;
    }

    this.pressedKeys.next(updatedKeys);
  }

  private hasShortcut(keys: { [key: string]: boolean }): boolean {
    const control = 'Control' in keys;
    const enter = 'Enter' in keys;
    const meta = 'Meta' in keys;
    const space = ' ' in keys;
    return (control && enter) || (meta && enter) || (control && space);
  }

  private isOnlyKey(key: string): boolean {
    return Object.keys(this.pressedKeys.getValue()).length === 1 && this.pressedKeys.getValue()[key];
  }

  /**
   * Checks if the current key combination is in the preventDefaultKeyCombos set.
   * If it is, it prevents the default behavior of the key combination.
   */
  private preventDefaultOnKeyCombo() {
    // Alphabetize the keys and join them with commas since the preventDefaultKeyCombos set is a set of sorted strings.
    const currentKeyCombo = Object.keys(this.pressedKeys.getValue())
      .map(key => key.trim())
      .sort()
      .join(',');
    return this.preventDefaultKeyCombos.has(currentKeyCombo);
  }

  private subscribeToKeyClick() {
    this.keyDown$.subscribe(this.handleKeyDown);
    this.keyUp$.subscribe(this.handleKeyUp);
  }
}
